/*
 * Copyright (C) 2023 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  AbstractControl,
  FormArray,
  FormControl,
  FormControlStatus,
  FormGroup,
  ValidationErrors,
  ɵFormGroupRawValue, ɵFormGroupValue
} from '@angular/forms';
import { createNgfControlEnhancement } from '@ng-falcon/ngf-form/+model/enhancement/ngf-control-enhancement';
import { NgfFormGroupOptions } from '@ng-falcon/ngf-form/+model/ngf-form-group';
import { Observable } from 'rxjs';

type NgfFormOptions<T extends { [K in keyof T]: AbstractControl }> = NgfFormGroupOptions & {
  readonly autoEnableControlsWhenUsed?: boolean;
}
// TODO: uncomment when IntelliJ bug:
//       https://youtrack.jetbrains.com/issue/WEB-59896/Angular-completion-not-working-for-classes-extending-FormGroup-in-html-only
//       will be fixed
// export class NgfForm<T extends { [K in keyof T]: AbstractControl }> extends NgfFormGroup<T> {
//   readonly autoEnableControlsWhenUsed: boolean;
//
//   constructor(controls: T, opts?: NgfFormOptions) {
//     super(controls, opts);
//
//     this.autoEnableControlsWhenUsed = opts?.autoEnableControlsWhenUsed ?? true;
//     if (this.autoEnableControlsWhenUsed) {
//       this.disable();
//     }
//   }
//
// }

// TODO: remove when IntelliJ bug:
//       https://youtrack.jetbrains.com/issue/WEB-59896/Angular-completion-not-working-for-classes-extending-FormGroup-in-html-only
//       will be fixed
export class NgfForm<T extends { [K in keyof T]: AbstractControl }> extends FormGroup<T> {
  readonly value$: Observable<FormGroup<T>['value']>;
  readonly errors$: Observable<ValidationErrors | null>;
  readonly touched$: Observable<boolean>;
  readonly untouched$: Observable<boolean>;
  readonly pristine$: Observable<boolean>;
  readonly dirty$: Observable<boolean>;
  readonly status$: Observable<FormControlStatus>;
  readonly valid$: Observable<boolean>;
  readonly invalid$: Observable<boolean>;
  readonly pending$: Observable<boolean>;
  readonly enabled$: Observable<boolean>;
  readonly disabled$: Observable<boolean>;

  readonly autoEnableControlsWhenUsed: boolean;

  constructor(controls: T, opts?: NgfFormOptions<T>) {
    super(controls, opts);

    const ngfControlEnhancement = createNgfControlEnhancement(this);
    this.value$ = ngfControlEnhancement.value$;
    this.errors$ = ngfControlEnhancement.errors$;
    this.touched$ = ngfControlEnhancement.touched$;
    this.untouched$ = ngfControlEnhancement.untouched$;
    this.pristine$ = ngfControlEnhancement.pristine$;
    this.dirty$ = ngfControlEnhancement.dirty$;
    this.status$ = ngfControlEnhancement.status$;
    this.valid$ = ngfControlEnhancement.valid$;
    this.invalid$ = ngfControlEnhancement.invalid$;
    this.pending$ = ngfControlEnhancement.pending$;
    this.enabled$ = ngfControlEnhancement.enabled$;
    this.disabled$ = ngfControlEnhancement.disabled$;

    if (opts?.disabled) {
      this.disable();
    }

    this.autoEnableControlsWhenUsed = opts?.autoEnableControlsWhenUsed ?? true;
    if (this.autoEnableControlsWhenUsed) {
      this.disable();
    }
  }

  override setValue(value: ɵFormGroupRawValue<T>, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.setValue(value, options);
    this.markAllAsPristine();
  }

  override patchValue(value: ɵFormGroupValue<T>, options?: { onlySelf?: boolean; emitEvent?: boolean }): void {
    super.patchValue(value, options);
    this.markAllAsPristine();
  }

  markAllAsPristine(): void {
    const controls: AbstractControl[] = Object.values(this.controls);

    for (let i = 0; i < controls.length; i++) {
      const control = controls[i];
      if (control instanceof FormGroup) {
        controls.push(...Object.values(control.controls));
      } else if (control instanceof FormArray) {
        controls.push(...control.controls);
      } else if (control instanceof FormControl) {
        control.markAsPristine();
      } else {
        // in case Angular will add more control classes in the future
        throw Error(`[NgfForm] Unknown abstract control class: ${control.constructor.name}`);
      }
    }
  }

}


